import React, { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

import { styled } from "@mui/material/styles";

const STATUS_CONFIG = {
  success: {
    icon: <CheckCircleIcon sx={{ fontSize: "4.3rem" }} color="success" />,
    header:
      "You have successfully verified your account. Please login to continue.",
    buttonText: "Go to Login",
  },
  pending: {
    icon: <MailOutlineIcon sx={{ fontSize: "4.3rem" }} color="info" />,
    header: "Verification Email Sent",
    buttonText: "Back to Login",
  },
  error: {
    icon: <ErrorIcon sx={{ fontSize: "4.3rem" }} color="error" />,
    header: "Verification Failed",
    buttonText: "Back to Login",
  },
};

export const Styled = styled(Box, {
  shouldForwardProp: (prop) => prop !== "status",
})(({ theme, status }) => {
  const isLightMode = theme.palette.mode === "light";

  const colorMap = {
    success: theme.palette.primary,
    pending: theme.palette.info,
    error: theme.palette.error,
  };

  const palette = colorMap[status] || theme.palette.error;
  const borderTopColor = isLightMode ? palette.light : palette.dark;

  return {
    "& .paper": {
      padding: "1rem",
      borderTop: "4px solid",
      borderTopColor,
    },
    "& .iconBox": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    "& .header": { fontSize: "1.25rem" },
    "& .message": { fontSize: ".9rem" },
    "& .errorMessage": {
      fontSize: ".95rem",
      fontWeight: 500,
      color: theme.palette.error.main,
      marginBottom: "0.5rem",
    },
    "& .helpText": {
      fontSize: ".85rem",
      color: theme.palette.text.secondary,
      marginTop: "0.8rem",
    },
    "& .loginButton": {
      marginTop: "1rem",
    },
  };
});

function SuccessContent() {
  return (
    <Typography className="message">
      Your account has been verified successfully. You can now login to access
      your account and continue with your applications.
    </Typography>
  );
}

function PendingContent() {
  return (
    <>
      <Typography className="message">
        Please check your email for a verification link to complete your account
        setup.
      </Typography>
      <Typography className="helpText">
        The email may take a few minutes to arrive. Check your spam folder if
        you don’t see it.
      </Typography>
    </>
  );
}

function ErrorContent({ errorMessage }) {
  return (
    <>
      <Typography className="errorMessage">{errorMessage}</Typography>
      <Typography className="message">
        We were unable to verify your account. This may be due to an invalid or
        expired verification link.
      </Typography>
      <Typography className="message" sx={{ mt: "0.8rem" }}>
        Please try logging in again or contact support if the issue persists.
      </Typography>
    </>
  );
}

function SSOVerificationStatus() {
  const { status } = useParams();
  const [searchParams] = useSearchParams();

  // Normalize status
  const normalizedStatus = ["success", "pending"].includes(status)
    ? status
    : "error";

  const config = STATUS_CONFIG[normalizedStatus];

  const errorMessage = useMemo(
    () =>
      searchParams.get("error") ||
      searchParams.get("message") ||
      "Verification failed. Please try again.",
    [searchParams]
  );

  const renderContent = () => {
    switch (normalizedStatus) {
      case "success":
        return <SuccessContent />;
      case "pending":
        return <PendingContent />;
      default:
        return <ErrorContent errorMessage={errorMessage} />;
    }
  };

  return (
    <Styled status={normalizedStatus}>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ pt: "5rem" }}
      >
        <Grid item xs={11} sm={10} md={5.5}>
          <Paper elevation={3} square className="paper">
            <Box display="flex">
              <Box className="iconBox" sx={{ width: "20%" }}>
                {config.icon}
              </Box>

              <Box sx={{ width: "80%", px: 1, py: 2 }}>
                <Typography variant="h6" className="header">
                  {config.header}
                </Typography>

                <Divider sx={{ my: "1rem" }} />

                {renderContent()}

                <Box className="loginButton">
                  <Button
                    component={Link}
                    to="/login"
                    variant="contained"
                    color="primary"
                  >
                    {config.buttonText}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Styled>
  );
}

export default SSOVerificationStatus;
